import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const AWS_REGION = process.env.REACT_APP_AWS_REGION || "us-east-2";
const AWS_API_KEY = process.env.REACT_APP_AWS_API_KEY || "";
const AWS_MAP_STYLE = process.env.REACT_APP_AWS_MAP_STYLE || "Standard";
const MAP_STYLE_URL = `https://maps.geo.${AWS_REGION}.amazonaws.com/v2/styles/${AWS_MAP_STYLE}/Light/descriptor?key=${AWS_API_KEY}`;

// Custom green brand-colored SVG pin (URL-encoded for inline CSS)
const GREEN_PIN_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 30'%3E%3Cfilter id='s'%3E%3CfeDropShadow dx='0' dy='1' stdDeviation='1.5' flood-opacity='0.3'/%3E%3C/filter%3E%3Cg filter='url(%23s)'%3E%3Cpath d='M12 0C5.37 0 0 5.37 0 12c0 9 12 18 12 18s12-9 12-18C24 5.37 18.63 0 12 0z' fill='%2354ac34'/%3E%3Ccircle cx='12' cy='12' r='4.5' fill='white'/%3E%3C/g%3E%3C/svg%3E")`;

const StoreMapModal = ({ store, onClose }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  const coords = store?.location?.coordinates ?? store?.store_coordinates; // [lng, lat] — GeoJSON order
  const hasCoords = Array.isArray(coords) && coords.length === 2;
  const lng = hasCoords ? coords[0] : null;
  const lat = hasCoords ? coords[1] : null;

  const addressLine1 = [store?.address1, store?.address2]
    .filter(Boolean)
    .join(", ");
  const pincode = Array.isArray(store?.pincode)
    ? store.pincode.join(" ")
    : store?.pincode || "";
  const city = store?.cities?.[0]?.name || "";
  const county = store?.counties?.[0]?.name || "";
  const state = store?.states?.[0]?.name || "";
  const country = store?.countries?.[0]?.name || "";
  const addressLine2 = [city, county, state, pincode]
    .filter(Boolean)
    .join(", ");
  const addressLine3 = country;
  const fullAddress = [addressLine1, addressLine2, addressLine3]
    .filter(Boolean)
    .join(", ");

  // Initialise the map once
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current || !hasCoords) return;

    const mgl = window.maplibregl;
    if (!mgl || !AWS_API_KEY) return;

    let map;
    try {
      map = new mgl.Map({
        container: mapContainerRef.current,
        style: MAP_STYLE_URL,
        center: [lng, lat],
        zoom: 15,
        attributionControl: false,
      });

      map.addControl(
        new mgl.NavigationControl({ showCompass: false }),
        "top-right",
      );
      map.addControl(
        new mgl.AttributionControl({ compact: true }),
        "bottom-right",
      );

      map.on("load", () => {
        const el = document.createElement("div");
        el.style.cssText = `
          width: 32px; height: 40px; cursor: default;
          background: ${GREEN_PIN_SVG} no-repeat center / contain;
          filter: drop-shadow(0 2px 4px rgba(84,172,52,0.5));
        `;
        markerRef.current = new mgl.Marker({ element: el, anchor: "bottom" })
          .setLngLat([lng, lat])
          .addTo(map);
      });

      mapRef.current = map;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[StoreMapModal] Failed to init map:", err);
    }

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      if (map) map.remove();
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!store) return null;

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        background: "rgba(0,0,0,0.52)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        animation: "fadeIn 0.15s ease",
      }}
    >
      {/* Modal card */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(460px, 100%)",
          borderRadius: "20px",
          overflow: "hidden",
          background: "#ffffff",
          boxShadow: "0 24px 80px rgba(0,0,0,0.3), 0 8px 24px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "min(600px, 90vh)",
          animation: "slideUp 0.2s ease",
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "14px 16px",
            borderBottom: "1px solid rgba(0,0,0,0.07)",
            background: "rgba(255,255,255,0.97)",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: "10px",
              background: "rgba(84,172,52,0.1)",
              border: "1px solid rgba(84,172,52,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <StorefrontOutlinedIcon
              sx={{ fontSize: "1.15rem", color: "#54ac34" }}
            />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: "0.95rem",
                color: "#1a1a1a",
                lineHeight: 1.3,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {store.company_name}
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "rgba(0,0,0,0.06)",
              cursor: "pointer",
              borderRadius: "50%",
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(0,0,0,0.12)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(0,0,0,0.06)")
            }
          >
            <CloseIcon sx={{ fontSize: "1rem", color: "#555" }} />
          </button>
        </div>

        {/* ── Map or no-coords placeholder ── */}
        {hasCoords ? (
          <div
            ref={mapContainerRef}
            style={{ flex: 1, minHeight: 0, height: 420 }}
          />
        ) : (
          <div
            style={{
              height: 200,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              background:
                "linear-gradient(135deg, rgba(84,172,52,0.04) 0%, rgba(255,255,255,0.6) 100%)",
              color: "#aaa",
            }}
          >
            <LocationOnIcon
              sx={{ fontSize: "2.8rem", color: "rgba(84,172,52,0.35)" }}
            />
            <span style={{ fontSize: "0.83rem", color: "#999" }}>
              No map coordinates available for this store.
            </span>
          </div>
        )}

        {/* ── Business card footer ── */}
        <div
          style={{
            padding: "16px 18px",
            background: "rgba(248,250,248,0.97)",
            borderTop: "1px solid rgba(0,0,0,0.06)",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            flexShrink: 0,
          }}
        >
          {/* Address row */}
          {(addressLine1 || addressLine2 || addressLine3) && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "rgba(84,172,52,0.1)",
                  border: "1px solid rgba(84,172,52,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                <LocationOnIcon
                  sx={{ fontSize: "0.85rem", color: "#54ac34" }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {addressLine1 && (
                  <span
                    style={{
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: "#222",
                      lineHeight: 1.5,
                    }}
                  >
                    {addressLine1}
                  </span>
                )}
                {addressLine2 && (
                  <span
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: "#555",
                      lineHeight: 1.5,
                    }}
                  >
                    {addressLine2}
                  </span>
                )}
                {addressLine3 && (
                  <span
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 500,
                      color: "#888",
                      lineHeight: 1.5,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {addressLine3}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Phone row */}
          {store.mobile && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "rgba(0,174,239,0.1)",
                  border: "1px solid rgba(0,174,239,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <PhoneAndroidIcon
                  sx={{ fontSize: "0.8rem", color: "#00AEEF" }}
                />
              </div>
              <span
                style={{ fontSize: "0.78rem", fontWeight: 600, color: "#333" }}
              >
                {store.mobile}
              </span>
            </div>
          )}

          {/* Open in Maps button — full width */}
          {hasCoords && (
            <a
              href={`https://www.google.com/maps/search/${encodeURIComponent(
                [store.company_name, fullAddress].filter(Boolean).join(", "),
              )}/@${lat},${lng},17z`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                marginTop: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "9px 0",
                borderRadius: "10px",
                background: "linear-gradient(135deg, #54ac34 0%, #4a9b2e 100%)",
                color: "#fff",
                fontSize: "0.8rem",
                fontWeight: 600,
                textDecoration: "none",
                boxShadow: "0 2px 8px rgba(84,172,52,0.35)",
                transition: "box-shadow 0.15s, transform 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(84,172,52,0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 2px 8px rgba(84,172,52,0.35)";
              }}
            >
              <OpenInNewIcon sx={{ fontSize: "0.85rem" }} />
              Open in Maps
            </a>
          )}
        </div>
      </div>

      {/* Keyframe animations injected once */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
      `}</style>
    </div>,
    document.body,
  );
};

StoreMapModal.propTypes = {
  store: PropTypes.shape({
    company_name: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    pincode: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    mobile: PropTypes.string,
    store_coordinates: PropTypes.arrayOf(PropTypes.number),
  }),
  onClose: PropTypes.func.isRequired,
};

export default StoreMapModal;
